import { useEffect, useRef, useState } from 'react'
import Select from 'react-select'
import { UserOption } from './RemoveUser'
import {
  AllInvestorsQuery,
  GetAllInvestorComapniesQuery,
  useAddUserToInvestorMutation,
  useAddMultipleInvestorsToUserMutation,
  useAllInvestorsQuery,
  useGetAllInvestmentsQuery,
  useGetAllInvestorComapniesQuery,
  useGetInvestorsQuery,
  useAddMultipleInvestorToMultipleUserMutation,
  useGetAllTransactionsQuery,
  useGetAllUsersOfAccountQuery,
} from '../../../../generated/graphql'
import { graphqlRequestClient, queryClient } from '../../../../queries/client'
import { selectCustomStyles } from '../../../../utils/Select'
import { useFormik } from 'formik'
import Toast, { ToastType } from '../../../../utils/toast'
import { KTSVG } from '../../../../_metronic/helpers/components/KTSVG'
import { addAnotherUserSchema } from '../AccountHelper'
import { roles } from '../utils'
import { ShowInfoPopover } from '../../../../utils/InfoPopover'
import * as Yup from 'yup'
import { getUserId } from '../../../../utils/SessionStorageUtils'

interface AddUserPopupProps {
  show: boolean
  onHide: () => void
  allUsers: UserOption[]
  accountId: string
}

type companyData = {
  companyName: string
  companyId: string
}

type options = {
  value: number
  label: string
  index?: number
}

type fund = {
  name: string
  id: number
}

const existingUserSchema = Yup.object({})

const newUserSchema = Yup.object({
  emailId: Yup.string().email('Please enter a valid email address').required('Email is required'),
  isAdmin: Yup.string().required('Role is required'),
})

export const AddUserPopup: React.FC<AddUserPopupProps> = ({
  show,
  onHide,
  allUsers,
  accountId,
}) => {
  const [isExistingUser, setIsExistingUser] = useState(true)
  const [selectedExistingUsers, setSelectedExistingUsers] = useState<UserOption[]>([])
  const [table, setTable] = useState(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [fund, setFund] = useState<fund>()
  const [investorList, setInvestorList] = useState<options[]>([])
  const [investorIdList, setInvestorIdList] = useState<number[]>([])
  const [investorName, setInvestorName] = useState<string>()
  const [access, setAccess] = useState<companyData[]>([])
  const [isAccountAdmin, setIsAccountAdmin] = useState<boolean>(false)
  const [showValidationErrors, setShowValidationErrors] = useState(false)
  const multiSelectRef = useRef<any>(null)
  const currentUserId = getUserId()
  const mutateAddMultipleUser = useAddMultipleInvestorsToUserMutation<Error>(
    graphqlRequestClient,
    {},
    {}
  )
  const mutateAddMultipleInvestorsToMultipleUsers =
    useAddMultipleInvestorToMultipleUserMutation<Error>(graphqlRequestClient, {}, {})
  const formik = useFormik({
    initialValues: {
      emailId: '',
      companies: '',
      isAdmin: '',
      isAccountAdmin: false,
    },
    validationSchema: isExistingUser ? existingUserSchema : newUserSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values) => {
      setShowValidationErrors(true)
      setLoading(true)
      if (isExistingUser) {
        if (selectedExistingUsers.length === 0 || investorList.length === 0) {
          setLoading(false)
          Toast('Please select users and funds', ToastType.error)
          return
        }

        const userIds = selectedExistingUsers.map((user) => Number(user.value))
        mutateAddMultipleInvestorsToMultipleUsers.mutate(
          {
            input: {
              userIds,
              investorIds: investorIdList,
            },
          },
          {
            onSuccess: (result) => {
              setLoading(false)
              queryClient.invalidateQueries(useGetInvestorsQuery.getKey())
              queryClient.invalidateQueries(useGetAllInvestmentsQuery.getKey())
              queryClient.invalidateQueries(useGetAllUsersOfAccountQuery.getKey())
              const investors = result?.addMultipleInvestorsToMultipleUsers || []
              const affectedInvestorsCount = investors

              Toast(
                `Successfully assigned ${userIds.length} user(s) to ${affectedInvestorsCount} fund(s)`,
                ToastType.success
              )
              handleClose()
            },
            onError: (error: any) => {
              setLoading(false)
              const errorMessage =
                error?.response?.errors?.[0]?.message || 'Failed to add users to investors'
              Toast(errorMessage, ToastType.error)
            },
          }
        )
        return
      }

      // Handle New User Flow
      if (investorList.length === 0) {
        setLoading(false)
        Toast('Please select at least one fund', ToastType.error)
        return
      }

      // Use the new API for multiple investors
      mutateAddMultipleUser.mutate(
        {
          input: {
            accountId: Number(accountId),
            userEmailId: values.emailId.trim(),
            isAdmin: isAccountAdmin,
            investorIds: investorIdList,
            role: roles.find((role) => role.label === values.isAdmin)?.value,
          },
        },
        {
          onSuccess: async (result) => {
            setLoading(false)
            await queryClient.invalidateQueries(useGetAllUsersOfAccountQuery.getKey())
            await queryClient.invalidateQueries(useGetInvestorsQuery.getKey())
            await queryClient.invalidateQueries(useGetAllInvestmentsQuery.getKey())

            const investorsAdded = result?.addUserToMultipleInvestors || []
            Toast(
              `User added successfully to ${investorsAdded.length} investor(s)`,
              ToastType.success
            )
            handleClose()
          },
          onError: (error: any) => {
            setLoading(false)
            const errorMessage = error?.response?.errors?.[0]?.message || 'Failed to add user'
            Toast(errorMessage, ToastType.error)
          },
        }
      )
    },
  })

  const { isSuccess, data } = useAllInvestorsQuery<AllInvestorsQuery, Error>(
    graphqlRequestClient,
    {
      input: {
        accountName: '',
        accountId: Number(accountId),
      },
    },
    { enabled: accountId !== undefined }
  )

  const allCompanies = useGetAllInvestorComapniesQuery<GetAllInvestorComapniesQuery>(
    graphqlRequestClient,
    { input: { investorId: fund?.id, investorName: fund?.name } },
    {
      enabled: fund !== undefined,
    }
  )

  const fundOptions: options[] = []
  if (isSuccess) {
    data?.getAllInvestors.forEach((fund: fund, index: number) => {
      if (fund.name !== '') {
        fundOptions.push({ value: fund.id, label: fund.name, index: index })
      }
    })
  }

  const roleOptions = roles
  const companies: companyData[] = []

  useEffect(() => {
    if (
      allCompanies?.data &&
      allCompanies?.data.allInvestorCompanies &&
      allCompanies?.data.allInvestorCompanies.companies
    ) {
      const newCompanies: companyData[] = []
      allCompanies?.data?.allInvestorCompanies.companies.hissa.forEach((h: companyData) => {
        newCompanies.push(h)
      })
      allCompanies?.data?.allInvestorCompanies.companies.manual.forEach((m: companyData) => {
        newCompanies.push(m)
      })
      setAccess(newCompanies)
    }
  }, [allCompanies.data])

  if (
    allCompanies?.data &&
    allCompanies?.data.allInvestorCompanies &&
    allCompanies?.data.allInvestorCompanies.companies
  ) {
    allCompanies?.data?.allInvestorCompanies.companies.hissa.forEach((h: companyData) => {
      companies.push(h)
    })
    allCompanies?.data?.allInvestorCompanies.companies.manual.forEach((m: companyData) => {
      companies.push(m)
    })
  }

  const tableData = companies.map((company: companyData) => {
    return (
      <tr key={`${company.companyName}`}>
        <td>
          {company.companyName}{' '}
          {company.companyId !== '' && (
            <KTSVG path='/media/icons/duotune/general/gen043.svg' className=' text-primary' />
          )}
        </td>
        <td>
          <Select
            options={[{ value: 'Admin', label: 'Admin' }]}
            styles={selectCustomStyles}
            defaultInputValue='Admin'
          />
        </td>
        <td>
          <Select
            styles={selectCustomStyles}
            options={[
              { value: 'Yes', label: 'Yes' },
              { value: 'No', label: 'No' },
            ]}
            defaultValue={[{ value: 'Yes', label: 'Yes' }]}
            onChange={(e: any) => {
              setAccess((prevAccess) => {
                const newAccess = new Set(prevAccess)
                if (e.value === 'Yes') {
                  newAccess.add(company)
                } else if (e.value === 'No') {
                  newAccess.delete(company)
                }
                return Array.from(newAccess)
              })
            }}
          />
        </td>
      </tr>
    )
  })

  // Handle mode toggle and clear all fields
  const handleUserTypeChange = (isExisting: boolean) => {
    setIsExistingUser(isExisting)

    // Clear formik fields
    formik.resetForm()

    // Clear all state
    setSelectedExistingUsers([])
    setInvestorList([])
    setInvestorIdList([])
    setInvestorName(undefined)
    setFund(undefined)
    setAccess([])
    setIsAccountAdmin(false)
    setTable(false)
    setShowValidationErrors(false)

    // Clear the multi-select dropdown
    if (multiSelectRef.current) {
      multiSelectRef.current.clearValue()
    }
  }

  // Handle close and reset everything
  const handleClose = () => {
    formik.resetForm()
    setIsExistingUser(true)
    setSelectedExistingUsers([])
    setInvestorList([])
    setInvestorIdList([])
    setInvestorName(undefined)
    setFund(undefined)
    setAccess([])
    setIsAccountAdmin(false)
    setTable(false)
    setLoading(false)
    setShowValidationErrors(false)
    onHide()
  }

  if (!show) return null

  return (
    <div
      className='modal fade show d-block'
      tabIndex={-1}
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
    >
      <div className='modal-dialog modal-dialog-centered modal-lg'>
        <div className='modal-content'>
          <div className='modal-header'>
            <div className='col d-flex justify-content-between px-1'>
              <div className='py-3'>
                <h3 className='fw-bold text-dark'>Add User</h3>
              </div>
              <button
                className='btn btn-sm btn-icon btn-active-light-primary mt-0'
                type='button'
                data-bs-toggle='tooltip'
                title='Close'
                onClick={handleClose}
                disabled={loading}
              >
                <KTSVG className='svg-icon-1' path='/media/icons/duotune/arrows/arr061.svg' />
              </button>
            </div>
          </div>

          <form onSubmit={formik.handleSubmit}>
            <div className='modal-body'>
              {/* User Type Selection */}
              <div className='mb-4'>
                <label className='form-label fw-semibold'>User Type</label>
                <Select
                  options={[
                    { value: 'existing', label: 'Existing User' },
                    { value: 'new', label: 'New User' },
                  ]}
                  value={{
                    value: isExistingUser ? 'existing' : 'new',
                    label: isExistingUser ? 'Existing User' : 'New User',
                  }}
                  onChange={(e: any) => handleUserTypeChange(e.value === 'existing')}
                  styles={selectCustomStyles}
                />
              </div>

              {isExistingUser ? (
                <>
                  {/* Existing User Selection */}
                  <div className='mb-4'>
                    <label className='form-label fw-semibold required'>Select Users</label>
                    <Select<UserOption, true>
                      options={allUsers.filter((user) => user.value !== String(currentUserId))}
                      getOptionLabel={(option) => option.name}
                      getOptionValue={(option) => option.value}
                      value={selectedExistingUsers}
                      onChange={(selected) => setSelectedExistingUsers(selected as UserOption[])}
                      isMulti
                      closeMenuOnSelect={false}
                      styles={selectCustomStyles as any}
                      placeholder='Select users...'
                    />
                    {showValidationErrors && selectedExistingUsers.length === 0 && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>Please select at least one user</div>
                      </div>
                    )}
                  </div>

                  {/* Investor/Fund Selection for Existing Users */}
                  <div className='mb-5'>
                    <label className='form-label fw-semibold required'>
                      Select the Investor/Fund
                    </label>
                    <Select
                      ref={multiSelectRef}
                      options={fundOptions}
                      value={investorList}
                      onChange={(e: any) => {
                        setInvestorList(e)
                        setInvestorIdList(e.map((element: options) => Number(element.value)))
                        if (e.length === 1) {
                          setInvestorName(e[0].label)
                          setFund({ name: e[0].label, id: e[0].value })
                        }
                        if (e.length !== 1) {
                          setInvestorName(undefined)
                          setFund(undefined)
                        }
                        setTable(false)
                        setAccess([])
                      }}
                      isMulti
                      closeMenuOnSelect={false}
                      styles={selectCustomStyles}
                      placeholder='Select funds...'
                    />
                    {showValidationErrors && investorList.length === 0 && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>Please select at least one fund</div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  {/* Investor/Fund Selection for New Users */}
                  <div className='mb-4'>
                    <label className='form-label fw-semibold required'>
                      Select the Investor/Fund
                    </label>
                    <Select
                      ref={multiSelectRef}
                      options={fundOptions}
                      value={investorList}
                      onChange={(e: any) => {
                        setInvestorList(e)
                        setInvestorIdList(e.map((element: options) => Number(element.value)))
                        if (e.length === 1) {
                          setInvestorName(e[0].label)
                          setFund({ name: e[0].label, id: e[0].value })
                        }
                        if (e.length !== 1) {
                          setInvestorName(undefined)
                          setFund(undefined)
                        }
                        setTable(false)
                        setAccess([])
                      }}
                      isMulti
                      closeMenuOnSelect={false}
                      styles={selectCustomStyles}
                      placeholder='Select funds...'
                    />
                    {showValidationErrors && investorList.length === 0 && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>Please select at least one fund</div>
                      </div>
                    )}
                  </div>

                  {/* Email Address */}
                  <div className='mb-4'>
                    <label className='form-label fw-semibold required'>Email Address</label>
                    <input
                      className={`form-control ${
                        showValidationErrors && formik.touched.emailId && formik.errors.emailId
                          ? 'is-invalid'
                          : ''
                      }`}
                      type='email'
                      autoComplete='off'
                      placeholder='Email Address'
                      {...formik.getFieldProps('emailId')}
                    />
                    {showValidationErrors && formik.touched.emailId && formik.errors.emailId && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>{formik.errors.emailId}</div>
                      </div>
                    )}
                  </div>

                  {/* Role Selection */}
                  <div className='mb-5'>
                    <label className='form-label fw-semibold required'>Role</label>
                    <Select
                      options={roleOptions}
                      styles={selectCustomStyles}
                      name='isAdmin'
                      value={
                        roleOptions.find((role) => role.label === formik.values.isAdmin) || null
                      }
                      onChange={(e: any) => {
                        formik.setFieldValue('isAdmin', e.label)
                        if (e.label === 'Admin') {
                          setIsAccountAdmin(true)
                        } else {
                          setIsAccountAdmin(false)
                        }
                      }}
                      placeholder='Select a role...'
                    />
                    {showValidationErrors && formik.touched.isAdmin && formik.errors.isAdmin && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>{formik.errors.isAdmin}</div>
                      </div>
                    )}
                  </div>
                </>
              )}

              <div className='d-flex justify-content-end gap-3 mt-4'>
                <button
                  type='button'
                  className='btn btn-light'
                  onClick={handleClose}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button type='submit' className='btn btn-primary' disabled={loading}>
                  {loading ? (
                    <>
                      <span className='spinner-border spinner-border-sm me-2' />
                      Adding...
                    </>
                  ) : (
                    'Add User'
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
